<template>
  <div class="upload-wrapper" @paste="handlePaste">
    <el-upload
      class="avatar-uploader"
      action="#"
      :limit="limit"
      :file-list="fileList"
      :accept="accept"
      :before-upload="beforeUpload"
      :on-remove="handleRemove"
      :http-request="customUpload"
      :on-exceed="handleExceed"
      :on-preview="handlePictureCardPreview"
      list-type="picture-card"
      :multiple="enableMultiple"
      ref="uploadRef"
    >
      <el-button size="small" type="primary">点击上传</el-button>
      <div slot="tip" class="el-upload__tip">
        {{ tipText || (enablePaste ? '点击上传或粘贴上传' : '请上传文件，且不超过10MB') }}
      </div>
    </el-upload>
    <div>
      <slot></slot>
    </div>
    <el-dialog
      :visible.sync="dialogVisible"
      title="预览"
      :append-to-body="true"
    >
      <img width="100%" :src="dialogImageUrl" alt="" />
    </el-dialog>
  </div>
</template>

<script>
import {
  readImageClipboardMeta,
  resolvePastedImageFileName,
} from "@/utils/imageClipboard";

export default {
  name: "UpLoad",
  props: {
    enablePaste: {
      type: Boolean,
      default: true,
    },
    enableMultiple: {
      type: Boolean,
      default: true,
    },
    previewBaseUrl: {
      type: String,
      default: "/api/attachment/preview-image?path=",
    },
    customUploadDefault: {
      type: Boolean,
      default: true,
    },
    // 接受上传的文件类型
    accept: {
      type: String,
      default:
        ".jpg,.jpeg,.png,.webp,.bmp,.tiff,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.mp4,.mp3",
    },
    // 单个文件大小限制(MB)
    fileSize: {
      type: Number,
      default: 10,
    },
    // 提示文字
    tipText: {
      type: String,
      default: "",
    },
    // 最大允许上传个数
    limit: {
      type: Number,
      default: 5,
    },
    editFile: {
      //编辑时回显
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      fileList: [],
      imageUrl: "",
      dialogImageUrl: "",
      dialogVisible: false,
    };
  },
  watch: {
    fileList: {
      handler(newVal) {
        if (!Array.isArray(newVal)) {
          return;
        }
        newVal.forEach((item) => {
          if (!item) {
            return;
          }
          const normalizedUrl = this.normalizePreviewUrl(item.url || item.fileUrl);
          if (
            typeof normalizedUrl === "string" &&
            normalizedUrl &&
            normalizedUrl !== item.url
          ) {
            this.$set(item, "url", normalizedUrl);
          }
          if (!item.uid && item.id) {
            this.$set(item, "uid", item.id);
          }
        });
      },
      deep: true,
    },
    editFile: {
      handler(newVal) {
        if (!Array.isArray(newVal)) {
          this.fileList = [];
          return;
        }
        this.fileList = newVal.map((item, index) => {
          const clone = { ...item };
          const normalizedUrl = this.normalizePreviewUrl(clone.url || clone.fileUrl);
          if (typeof normalizedUrl === "string" && normalizedUrl) {
            clone.url = normalizedUrl;
          }
          if (clone.uid == null) {
            const baseUid =
              clone.id != null
                ? clone.id
                : clone.url != null
                ? clone.url
                : `${Date.now()}_${index}`;
            clone.uid = baseUid;
          }
          return clone;
        });
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    normalizePreviewUrl(url) {
      if (!url) {
        return url;
      }
      if (typeof url !== "string") {
        return url;
      }
      if (
        /^https?:\/\//i.test(url) ||
        url.startsWith("data:") ||
        url.startsWith("blob:")
      ) {
        return url;
      }
      if (url.startsWith(this.previewBaseUrl)) {
        return url;
      }
      return `${this.previewBaseUrl}${url}`;
    },
    async handlePaste(event) {
      if (!this.enablePaste) {
        return;
      }
      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData || !clipboardData.items || clipboardData.items.length === 0) {
        return;
      }
      const imageItems = [];
      for (let i = 0; i < clipboardData.items.length; i += 1) {
        const item = clipboardData.items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          imageItems.push(item);
        }
      }
      if (imageItems.length === 0) {
        return;
      }

      const plainText = (clipboardData.getData("text/plain") || "").trim();
      const metaFileName = await readImageClipboardMeta(clipboardData.items);
      const files = [];
      imageItems.forEach((item) => {
        const blob = item.getAsFile();
        if (!blob) {
          return;
        }
        const ext = this.getExtensionByType(blob.type);
        const fileName = resolvePastedImageFileName({
          blob,
          plainText,
          metaFileName,
          ext,
        });
        files.push(new File([blob], fileName, { type: blob.type }));
      });
      if (files.length === 0) {
        return;
      }
      event.preventDefault();
      const uploadRef = this.$refs.uploadRef;
      const currentCount =
        uploadRef && Array.isArray(uploadRef.uploadFiles)
          ? uploadRef.uploadFiles.length
          : this.fileList.length;
      const availableSlots = this.limit - currentCount;
      if (availableSlots <= 0) {
        this.$message.warning(`当前只能选择 ${this.limit} 个文件`);
        return;
      }
      const filesToUpload = files.slice(0, availableSlots);
      if (files.length > availableSlots) {
        this.$message.warning(`粘贴的文件数量超出限制，仅保留前 ${availableSlots} 个`);
      }
      const readyFiles = [];
      filesToUpload.forEach((file) => {
        const allowUpload = this.beforeUpload(file);
        if (!allowUpload) {
          return;
        }
        if (uploadRef && typeof uploadRef.handleStart === "function") {
          uploadRef.handleStart(file);
          readyFiles.push(file);
        }
      });
      if (
        readyFiles.length > 0 &&
        uploadRef &&
        typeof uploadRef.submit === "function"
      ) {
        uploadRef.submit();
      }
    },
    getExtensionByType(type) {
      if (!type || !type.includes("/")) {
        return ".png";
      }
      const [, subtype] = type.split("/");
      if (!subtype) {
        return ".png";
      }
      if (subtype === "jpeg") {
        return ".jpg";
      }
      return `.${subtype}`;
    },
    clearFileList() {
      this.fileList = [];
    },
    handlePictureCardPreview(file) {
      if (!file || !file.url) {
        return;
      }

      const url = file.name.toLowerCase();
      const imgExts = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];

      const isImage = imgExts.some((ext) => url.endsWith(ext));

      if (!isImage) {
        return; // 不是图片，直接返回
      }

      this.dialogImageUrl = file.url;
      this.dialogVisible = true;
    },
    // 删除文件之前的钩子s
    handleRemove(file, fileList) {
      this.fileList = fileList;
      this.$emit("remove", file, fileList);
    },
    // 文件超出数量限制时的钩子
    handleExceed(files, fileList) {
      this.$message.warning(`当前只能选择 ${this.limit} 个文件`);
    },
    beforeUpload(file) {
      // 检查文件类型
      if (this.accept === "all") {
        return true;
      } else {
        const fileType = file.name
          .substring(file.name.lastIndexOf("."))
          .toLowerCase();
        const acceptTypes = this.accept.split(",");
        if (acceptTypes.indexOf(fileType) === -1) {
          this.$message.error(`只能上传 ${this.accept} 格式的文件!`);
          return false;
        }
      }
      // 检查文件大小
      const isLtSize = file.size / 1024 / 1024 < this.fileSize;
      if (!isLtSize) {
        this.$message.error(`上传文件大小不能超过 ${this.fileSize}MB!`);
        return false;
      }

      return true;
    },
    customUpload(option) {
      const uploadOption =
        option && Object.prototype.hasOwnProperty.call(option, "file")
          ? option
          : { file: option };
      if (!uploadOption.file) {
        this.$message.error("上传文件缺失，请重试");
        return;
      }
      if (this.customUploadDefault) {
        const data = new FormData();
        data.append("file", uploadOption.file);
        this.axios({
          url: "/attachment/upload",
          method: "post",
          data,
          headers: { "Content-Type": "multipart/form-data" },
        })
          .then((res) => {
            if (res.data.code === 200) {
              this.$emit("uploadSuccess", res.data.data);
              const uploadFile = uploadOption.file;
              if (uploadFile) {
                const uploadUid =
                  uploadFile.uid != null ? String(uploadFile.uid) : undefined;
                const target = this.fileList.find(
                  (item) => String(item.uid) === uploadUid
                );
                const responseData = res.data.data || {};
                const rawUrl =
                  responseData.previewUrl ||
                  responseData.fileUrl ||
                  responseData.url ||
                  responseData.path ||
                  (typeof responseData === "string" ? responseData : "");
                const previewUrl = this.normalizePreviewUrl(rawUrl);
                if (target && typeof previewUrl === "string" && previewUrl) {
                  this.$set(target, "url", previewUrl);
                }
                if (target && responseData && responseData.id) {
                  this.$set(target, "uid", responseData.id);
                }
              }
              if (typeof uploadOption.onSuccess === "function") {
                uploadOption.onSuccess(res.data, uploadOption.file);
              }
              this.$message.success(
                "上传成功,若有确定提交按钮，进行操作后，文件将会被提交"
              );
            } else {
              this.$message.error(res.data.message);
              if (typeof uploadOption.onError === "function") {
                uploadOption.onError(
                  res.data.message || "上传失败",
                  uploadOption.file
                );
              }
            }
          })
          .catch((error) => {
            this.$message.error("上传失败，请重试！");
            if (typeof uploadOption.onError === "function") {
              uploadOption.onError(error, uploadOption.file);
            }
          });
      } else {
        this.$emit("customUpload", option);
      }
    },
  },
};
</script>

<style scoped>
.error_info {
  color: #d72822;
  font-size: 12px;
}
</style>
